import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Button } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type FormEvent } from "react";

const PASSWORD_MIN_LENGTH = 12;

interface ForceChangePasswordFormProps {
  email: string;
  newPassword: string;
  setNewPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  loading: boolean;
  errorMessage?: string;
  onSubmit: () => Promise<void>;
  /** Returns to the email step to start over with a different account. */
  onCancel: () => void;
}

/**
 * Forced password-change screen shown when a user signs in with a temporary
 * password (Cognito FORCE_CHANGE_PASSWORD). The pending Cognito challenge is
 * completed by the parent's submit handler, which finishes sign-in.
 */
export function ForceChangePasswordForm({
  email,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  loading,
  errorMessage,
  onSubmit,
  onCancel,
}: ForceChangePasswordFormProps) {
  const { tu } = useTu("login");

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    await onSubmit();
  }

  return (
    <Box
      component="form"
      sx={{ display: "flex", flexDirection: "column", gap: 4 }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Text bold variant="h5" sx={{ textAlign: "center", color: "base.base" }}>
        {tu("Set a new password")}
      </Text>
      <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
        {tu("You signed in with a temporary password. Choose a new one to finish signing in.")}
      </Text>
      <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
        {tu("Email")}: {email}
      </Text>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Text semibold variant="body2" sx={{ color: "base.muted" }}>
          {tu("New password")}
        </Text>
        <TextInput
          fullWidth
          required
          autoComplete="new-password"
          disabled={loading}
          type="password"
          value={newPassword}
          inputProps={{ "aria-label": tu("New password") }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1rem",
              backgroundColor: "background.standout",
            },
          }}
          onChange={(event) => {
            setNewPassword(event.target.value);
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Text semibold variant="body2" sx={{ color: "base.muted" }}>
          {tu("Confirm new password")}
        </Text>
        <TextInput
          fullWidth
          required
          autoComplete="new-password"
          disabled={loading}
          type="password"
          value={confirmPassword}
          inputProps={{ "aria-label": tu("Confirm new password") }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1rem",
              backgroundColor: "background.standout",
            },
          }}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
          }}
        />
      </Box>

      <Text variant="caption" sx={{ color: "base.muted", textAlign: "center" }}>
        {tu(
          "Password must be at least {{minLength}} characters, include uppercase and lowercase letters, and not start or end with a space.",
          { values: { minLength: PASSWORD_MIN_LENGTH } }
        )}
      </Text>

      {errorMessage && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {errorMessage}
        </Text>
      )}

      <LoadingButton
        fullWidth
        type="submit"
        variant="primary"
        size="large"
        isLoading={loading}
        disabled={loading || !newPassword || !confirmPassword}
      >
        {tu("Set new password and sign in")}
      </LoadingButton>

      <Button
        variant="text"
        disabled={loading}
        sx={{ textTransform: "none", color: "primary.main", alignSelf: "center" }}
        onClick={onCancel}
      >
        {tu("Back to sign in")}
      </Button>
    </Box>
  );
}
